import { Box, Container, Fade, Stack, ThemeProvider } from "@mui/material";
import { LoadingSpinner } from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings, getTheme } from "@src/appV2/redesign/system/ui-theme";
import Lottie from "lottie-react";
import { type LottieRefCurrentProps } from "lottie-react";
import { useCallback, useEffect, useRef } from "react";

import { hideSplashScreen } from "../../../NativeAdapters/splashScreen.adapter";
import { PageWrapper } from "../../../redesign/components/PageWrapper";
import { isRebrandingEnabled } from "../../../redesign/system/ui-theme";
import { APP_V2_APP_EVENTS, logEvent } from "../../analytics";
import { ClipboardLogo } from "../../Content";
import ladderClimbLoopAnimation from "./animations/ladderClimbLoop.json";

interface AppLoadingPageProps {
  /**
   * Controls visibility. When toggled from `true` to `false`, the overlay
   * fades out and unmounts via MUI `<Fade>`. Defaults to `true`.
   */
  isLoading?: boolean;
  parentEventName?: string;
  isDebuggingEnabled?: boolean;
  debugTimeoutInMs?: number;
}

const LOTTIE_SPLASH_FALLBACK_TIMEOUT_MS = 5_000;

export function AppLoadingPage({
  isLoading = true,
  parentEventName,
  isDebuggingEnabled = false,
  debugTimeoutInMs = 20_000,
}: AppLoadingPageProps = {}) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const splashFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hasDismissedSplashRef = useRef(false);
  const theme = getTheme();

  const dismissSplashScreen = useCallback(async () => {
    if (hasDismissedSplashRef.current) {
      return;
    }
    hasDismissedSplashRef.current = true;

    if (splashFallbackTimeoutRef.current) {
      clearTimeout(splashFallbackTimeoutRef.current);
      splashFallbackTimeoutRef.current = undefined;
    }

    await hideSplashScreen();
  }, []);

  const handleLottieReady = useCallback(() => {
    // make sure the first frame is rendered before dismissing the splash screen
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        await dismissSplashScreen();
        lottieRef.current?.play();
      });
    });
  }, [dismissSplashScreen]);

  // Fallback: if Lottie never loads (e.g. animation data fails to parse,
  // onDOMLoaded never fires), force-dismiss the native splash so the user
  // isn't stuck on it indefinitely.
  useEffect(() => {
    splashFallbackTimeoutRef.current = setTimeout(() => {
      if (hasDismissedSplashRef.current) {
        return;
      }
      logEvent(APP_V2_APP_EVENTS.HIDE_SPLASH_SCREEN_LOTTIE_TIMEOUT, {
        parentEventName,
        timeoutMs: LOTTIE_SPLASH_FALLBACK_TIMEOUT_MS,
      });
      void dismissSplashScreen();
    }, LOTTIE_SPLASH_FALLBACK_TIMEOUT_MS);

    return () => {
      if (splashFallbackTimeoutRef.current) {
        clearTimeout(splashFallbackTimeoutRef.current);
        splashFallbackTimeoutRef.current = undefined;
      }
    };
  }, [dismissSplashScreen, parentEventName]);

  useEffect(() => {
    if (!isLoading) {
      return undefined;
    }

    logEvent(APP_V2_APP_EVENTS.APP_LOADING_PAGE_EFFECT_RUNNING, {
      parentEventName,
      isDebuggingEnabled,
      debugTimeoutInMs,
    });
    if (!isDebuggingEnabled || !parentEventName) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      logEvent(APP_V2_APP_EVENTS.APP_LOADING_TIMEOUT, {
        parentEventName,
        timeoutSeconds: debugTimeoutInMs / 1000,
        timestamp: new Date().toISOString(),
      });
    }, debugTimeoutInMs); // 20 seconds

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading, isDebuggingEnabled, parentEventName, debugTimeoutInMs]);

  // We are not using useLogEffect because we actually want to see all the instances this is rendered.
  // This is especially true to know what is happening when app move from background to foreground
  // Remove after splash screen investigation
  if (isLoading) {
    logEvent(APP_V2_APP_EVENTS.APP_LOADING_PAGE_DISPLAYED, {
      parentEventName,
      isDebuggingEnabled,
      debugTimeoutInMs,
    });
  }

  return (
    <ThemeProvider theme={theme}>
      <Fade
        unmountOnExit
        in={isLoading}
        appear={false}
        timeout={theme.transitions.duration.standard}
      >
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (muiTheme) => muiTheme.zIndex.modal,
            pointerEvents: isLoading ? "auto" : "none",
          }}
        >
          <PageWrapper
            sx={{
              background: (muiTheme) =>
                isRebrandingEnabled()
                  ? muiTheme.palette.brand.main
                  : muiTheme.palette.intent?.neutral.gradient,
              ...(isRebrandingEnabled()
                ? {}
                : getSafeAreaPaddings({ bottom: true, fallbackBottom: "24px" })),
              paddingX: 2,
            }}
          >
            <Container
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack
                spacing={8}
                alignItems="center"
                textAlign="center"
                sx={{
                  flex: 1,
                  justifyContent: "center",
                  mb: isRebrandingEnabled() ? 0 : 3,
                }}
              >
                {isRebrandingEnabled() ? (
                  <Box sx={{ width: 300, transform: "translate(-8px, 14px)" }}>
                    <Lottie
                      loop
                      lottieRef={lottieRef}
                      autoplay={false}
                      animationData={ladderClimbLoopAnimation}
                      onDOMLoaded={handleLottieReady}
                      onDataFailed={dismissSplashScreen}
                    />
                  </Box>
                ) : (
                  <>
                    <ClipboardLogo width={160} />
                    <LoadingSpinner size="large" variant="primary" />
                  </>
                )}
              </Stack>
            </Container>
          </PageWrapper>
        </Box>
      </Fade>
    </ThemeProvider>
  );
}
